import { useTranslation } from 'react-i18next'

interface LoadingStateProps {
  labelKey?: string
  compact?: boolean
  variant?: 'list' | 'cards' | 'detail' | 'profile' | 'form' | 'messages'
}

/** Decorative placeholders. The containing status supplies the announcement. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span className={`skeleton ${className}`} aria-hidden="true" />
}

export function LoadingState({
  labelKey = 'common.loading',
  compact = false,
  variant = 'list',
}: LoadingStateProps) {
  const { t } = useTranslation()
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={`loading-skeleton loading-skeleton--${compact ? 'compact' : variant}`}
    >
      <span className="sr-only">{t(labelKey)}</span>
      {compact ? (
        <div className="loading-skeleton__lines" aria-hidden="true">
          <Skeleton className="skeleton--line skeleton--title" />
          <Skeleton className="skeleton--line" />
        </div>
      ) : variant === 'cards' || variant === 'detail' ? (
        Array.from({ length: variant === 'detail' ? 1 : 3 }, (_, index) => (
          <div className="loading-skeleton__card" key={index} aria-hidden="true">
            <Skeleton className="skeleton--cover" />
            <div className="loading-skeleton__lines">
              <Skeleton className="skeleton--line skeleton--title" />
              <Skeleton className="skeleton--line" />
              <Skeleton className="skeleton--line skeleton--short" />
            </div>
          </div>
        ))
      ) : variant === 'form' ? (
        Array.from({ length: 4 }, (_, index) => (
          <div className="loading-skeleton__lines" key={index} aria-hidden="true">
            <Skeleton className="skeleton--line skeleton--short" />
            <Skeleton className="skeleton--field" />
          </div>
        ))
      ) : variant === 'messages' ? (
        Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} className="skeleton--message" />
        ))
      ) : (
        <>
          {variant === 'profile' ? <Skeleton className="skeleton--portrait" /> : null}
          {Array.from({ length: 3 }, (_, index) => (
            <div className="loading-skeleton__row" key={index} aria-hidden="true">
              {variant === 'list' ? <Skeleton className="skeleton--avatar" /> : null}
              <div className="loading-skeleton__lines">
                <Skeleton className="skeleton--line skeleton--title" />
                <Skeleton className="skeleton--line" />
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

export function FullPageLoader() {
  return (
    <main id="main-content" className="min-h-dvh bg-paper p-6">
      <LoadingState labelKey="common.loadingScreen" />
    </main>
  )
}
